// Lib
import { FC, useCallback, useEffect, useState } from "react";
// Api
import { useUpdateCustomerInternalNoteMutation } from "rtkQuery/query/customersAPI";
// Hooks
import { useNotification, useViewport } from "hooks";
// Utils
import { errorHandler } from "utils/errorHandler";
// Components
import { Modal } from "components";
import { TextArea } from "components/Form";
// Styled
import { FlexContainer } from "styled/Box";
import { Button } from "styled/Buttons";

interface CustomerNoteModalProps {
  id: string;
  note: string | null;
  isOpen: boolean;
  onClose: () => void;
}

export const CustomerNoteModal: FC<CustomerNoteModalProps> = ({
  isOpen,
  id,
  note,
  onClose,
}) => {
  const { isMobile } = useViewport();
  const { openNotification } = useNotification();

  const [updateNote, { isLoading }] = useUpdateCustomerInternalNoteMutation();

  const [value, setValue] = useState("");

  useEffect(() => {
    if (isOpen && note) {
      setValue(note);
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    if (isLoading) return;

    onClose();
  }, [isLoading]);

  const onCancel = useCallback(() => {
    setValue("");

    handleClose();
  }, [handleClose, setValue]);

  const onSave = useCallback(async () => {
    try {
      await updateNote({ id, note: value }).unwrap();

      setValue("");
      onClose();
    } catch (error) {
      errorHandler({ error, openNotification });
    }
  }, [value, id]);

  return (
    <Modal
      title="Internal note"
      open={isOpen}
      isLoading={isLoading}
      onClose={handleClose}
    >
      <TextArea
        label="Note"
        value={value}
        onChange={e => setValue(e.target.value || "")}
      />

      <FlexContainer
        $fullwidth
        $align="center"
        $justify="flex-end"
        $gap={8}
        $padding="24px 0 0"
        $column={isMobile}
      >
        <Button.Heading
          $fullWidth={isMobile}
          disabled={isLoading}
          onClick={onCancel}
        >
          Cancel
        </Button.Heading>

        <Button.Heading
          $fullWidth={isMobile}
          type="primary"
          loading={isLoading}
          onClick={onSave}
        >
          Save
        </Button.Heading>
      </FlexContainer>
    </Modal>
  );
};
